import { useEffect, useState } from 'react';
import apiFetch from '../utils/apiFetch';
import siteConfig from '../config/site-config.js';

function readStoredPermissions() {
  try {
    return JSON.parse(localStorage.getItem('permissions') || '[]');
  } catch (_) {
    return [];
  }
}

function readRoleFromToken(token) {
  try {
    if (!token) return '';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.role || '';
  } catch (_) {
    return '';
  }
}

/**
 * 管理后台登录 / 会话恢复
 */
export default function useAdminPanelAuth() {
  const [authed, setAuthed] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [role, setRole] = useState(() => localStorage.getItem('role') || readRoleFromToken(localStorage.getItem('token')) || '');
  const [adminName, setAdminName] = useState(localStorage.getItem('username') || '');
  const [userPermissions, setUserPermissions] = useState(() => readStoredPermissions());

  const API_BASE_URL = siteConfig.getApiUrl();

  const handleLogin = async () => {
    setLoginError('');
    try {
      const res = await apiFetch('/api/user/login', {
        method: 'POST',
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      const perms = Array.isArray(data.permissions) ? data.permissions : [];
      const canEnterAdmin = data.success && (
        data.role === 'admin' || perms.includes('管理后台访问')
      );
      if (canEnterAdmin) {
        setToken(data.token);
        setRole(data.role || 'user');
        setAuthed(true);
        setAdminName(username);
        setUserPermissions(perms);
        localStorage.setItem('token', data.token);
        localStorage.setItem('role', data.role || 'user');
        localStorage.setItem('username', username);
        localStorage.setItem('permissions', JSON.stringify(perms));
        try {
          if (Array.isArray(data.permissionConfig)) {
            localStorage.setItem('permissionConfig', JSON.stringify(data.permissionConfig));
          } else {
            const permConfigRes = await apiFetch('/api/user/permission-config');
            const permConfigData = await permConfigRes.json();
            if (permConfigData.success) {
              localStorage.setItem('permissionConfig', JSON.stringify(permConfigData.permissions || []));
            }
          }
        } catch (error) {
          console.error('获取权限配置失败:', error);
        }
        setLoginError('');
      } else if (data.success) {
        setLoginError('无权进入管理后台（需要系统管理员，或拥有「管理后台访问」权限）');
      } else {
        setLoginError(data.error || '无权访问管理后台');
      }
    } catch {
      setLoginError('网络错误');
    }
  };

  const handleLogout = () => {
    setAuthed(false);
    setToken('');
    setRole('');
    setAdminName('');
    setUserPermissions([]);
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('username');
    localStorage.removeItem('permissions');
  };

  useEffect(() => {
    const checkExistingAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUsername = localStorage.getItem('username');

      console.log('检查现有认证状态:', { hasToken: !!storedToken, hasUsername: !!storedUsername, authed });

      if (storedToken && storedUsername && !authed) {
        console.log('发现现有token，尝试自动认证...');
        try {
          const baseWithoutApi = API_BASE_URL.replace('/api', '');
          const response = await fetch(`${baseWithoutApi}/api/admin/users`, {
            headers: {
              'Authorization': 'Bearer ' + storedToken,
              'Content-Type': 'application/json'
            }
          });

          console.log('Token验证响应状态:', response.status);

          if (response.ok) {
            const restoredRole = localStorage.getItem('role') || readRoleFromToken(storedToken) || '';
            const restoredPermissions = readStoredPermissions();
            setToken(storedToken);
            setRole(restoredRole);
            setUserPermissions(restoredPermissions);
            setAuthed(true);
            setAdminName(storedUsername);
            if (restoredRole) localStorage.setItem('role', restoredRole);
            console.log('自动认证成功，将加载数据', { restoredRole, permCount: restoredPermissions.length });
          } else {
            console.log('Token已过期，需要重新登录');
            localStorage.removeItem('token');
            localStorage.removeItem('username');
            localStorage.removeItem('role');
            localStorage.removeItem('permissions');
          }
        } catch (error) {
          console.error('自动认证失败:', error);
        }
      } else if (!storedToken || !storedUsername) {
        console.log('没有存储的认证信息');
      } else if (authed) {
        console.log('已经认证过了');
      }
    };

    checkExistingAuth();
  }, [authed]);

  return {
    authed,
    username,
    setUsername,
    password,
    setPassword,
    loginError,
    token,
    role,
    adminName,
    userPermissions,
    handleLogin,
    handleLogout
  };
}
